import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Avatar,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  AccessTime as AccessTimeIcon,
  IntegrationInstructions as IntegrationIcon,
  PowerSettingsNew as PowerIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Block as BlockIcon,
  Info as InfoIcon,
  NewReleases as NewReleasesIcon,
  AddCircleOutline as NewFeatureIcon,
  AutoFixHigh as ImproveFeatureIcon,
  BugReport as FixFeatureIcon,
  RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { CHANGELOG, APP_VERSION, ChangeType } from '../config/changelog';
import ChangelogModal from '../components/ChangelogModal';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'SELLER' | 'DESIGNER' | 'SUPPORT' | 'MANAGER' | 'PRODUCAO';
  isActive: boolean;
  createdAt: string;
}

interface NewUser {
  name: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'SELLER' | 'DESIGNER' | 'SUPPORT' | 'MANAGER' | 'PRODUCAO';
}

interface Integration {
  id: string;
  name: string;
  type: string;
  isEnabled: boolean;
  config: string | null;
  createdAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ========================================
// COMPONENTE: Lojas IRROBA
// ========================================
interface IrrobaStore {
  id: string;
  name: string;
  apiUrl: string;
  isActive: boolean;
  isDefault: boolean;
  _count?: { designerStatus: number; orders: number };
}

interface Designer {
  id: string;
  name: string;
  email: string;
}

interface DesignerStatusMapping {
  id: string;
  storeId: string;
  statusId: number;
  statusName: string;
  designerId: string;
  designer: { id: string; name: string };
}

interface IrrobaStatus {
  id: number;
  name: string;
}

const IrrobaStoresTab: React.FC<{ onMessage: (msg: string, severity: 'success' | 'error') => void }> = ({ onMessage }) => {
  const [stores, setStores] = useState<IrrobaStore[]>([]);
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [selectedStore, setSelectedStore] = useState<IrrobaStore | null>(null);
  const [mappings, setMappings] = useState<DesignerStatusMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Status from IRROBA API
  const [availableStatuses, setAvailableStatuses] = useState<IrrobaStatus[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  
  // Form states
  const [openStoreDialog, setOpenStoreDialog] = useState(false);
  const [openMappingDialog, setOpenMappingDialog] = useState(false);
  const [storeForm, setStoreForm] = useState({ name: '', apiKey: '', apiSecret: '', apiUrl: 'https://api.irroba.com.br/v1', apiUsername: '' });
  const [mappingForm, setMappingForm] = useState({ statusId: '', statusName: '', designerId: '' });
  const [editingStore, setEditingStore] = useState<IrrobaStore | null>(null);

  // Load stores and designers
  useEffect(() => {
    loadData();
  }, []);

  // Load mappings when store is selected
  useEffect(() => {
    if (selectedStore) {
      loadMappings(selectedStore.id);
    }
  }, [selectedStore]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [storesData, designersData] = await Promise.all([
        apiService.listIrrobaStores(),
        apiService.listDesigners(),
      ]);
      setStores(storesData);
      setDesigners(designersData);
      if (storesData.length > 0 && !selectedStore) {
        setSelectedStore(storesData[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      onMessage('Erro ao carregar lojas IRROBA', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadMappings = async (storeId: string) => {
    try {
      const data = await apiService.listDesignerStatusMappings(storeId);
      setMappings(data);
    } catch (error) {
      console.error('Erro ao carregar mapeamentos:', error);
    }
  };

  const handleCreateStore = async () => {
    try {
      await apiService.createIrrobaStore({
        name: storeForm.name,
        apiKey: storeForm.apiKey,
        apiSecret: storeForm.apiSecret,
        apiUrl: storeForm.apiUrl,
        apiUsername: storeForm.apiUsername || undefined,
      });
      onMessage('Loja criada com sucesso!', 'success');
      setOpenStoreDialog(false);
      setStoreForm({ name: '', apiKey: '', apiSecret: '', apiUrl: 'https://api.irroba.com.br/v1', apiUsername: '' });
      loadData();
    } catch (error) {
      console.error('Erro ao criar loja:', error);
      onMessage('Erro ao criar loja', 'error');
    }
  };

  const handleUpdateStore = async () => {
    if (!editingStore) return;
    try {
      await apiService.updateIrrobaStore(editingStore.id, {
        name: storeForm.name,
        apiKey: storeForm.apiKey || undefined,
        apiSecret: storeForm.apiSecret || undefined,
        apiUrl: storeForm.apiUrl,
        apiUsername: storeForm.apiUsername || null,
      });
      onMessage('Loja atualizada com sucesso!', 'success');
      setOpenStoreDialog(false);
      setEditingStore(null);
      loadData();
    } catch (error) {
      console.error('Erro ao atualizar loja:', error);
      onMessage('Erro ao atualizar loja', 'error');
    }
  };

  const handleDeleteStore = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta loja? Isso removerá todos os mapeamentos e pedidos associados.')) return;
    try {
      await apiService.deleteIrrobaStore(id);
      onMessage('Loja excluída com sucesso!', 'success');
      if (selectedStore?.id === id) setSelectedStore(null);
      loadData();
    } catch (error) {
      console.error('Erro ao excluir loja:', error);
      onMessage('Erro ao excluir loja', 'error');
    }
  };

  const handleCreateMapping = async () => {
    if (!selectedStore) return;
    try {
      await apiService.upsertDesignerStatusMapping(selectedStore.id, {
        statusId: parseInt(mappingForm.statusId),
        statusName: mappingForm.statusName,
        designerId: mappingForm.designerId,
      });
      onMessage('Mapeamento criado com sucesso!', 'success');
      setOpenMappingDialog(false);
      setMappingForm({ statusId: '', statusName: '', designerId: '' });
      loadMappings(selectedStore.id);
    } catch (error) {
      console.error('Erro ao criar mapeamento:', error);
      onMessage('Erro ao criar mapeamento', 'error');
    }
  };

  const handleDeleteMapping = async (id: string) => {
    try {
      await apiService.deleteDesignerStatusMapping(id);
      onMessage('Mapeamento excluído!', 'success');
      if (selectedStore) loadMappings(selectedStore.id);
    } catch (error) {
      console.error('Erro ao excluir mapeamento:', error);
      onMessage('Erro ao excluir mapeamento', 'error');
    }
  };

  const handleFetchStatuses = async () => {
    if (!selectedStore) return;
    setLoadingStatuses(true);
    try {
      const statuses = await apiService.getStoreStatuses(selectedStore.id);
      setAvailableStatuses(statuses);
      if (statuses.length === 0) {
        onMessage('Nenhum status encontrado. Tente sincronizar pedidos primeiro.', 'error');
      } else {
        onMessage(`${statuses.length} status encontrados!`, 'success');
      }
    } catch (error) {
      console.error('Erro ao buscar status:', error);
      onMessage('Erro ao buscar status do IRROBA', 'error');
    } finally {
      setLoadingStatuses(false);
    }
  };

  const handleSyncOrders = async () => {
    if (!selectedStore) return;
    setSyncing(true);
    try {
      const result = await apiService.syncDesignerOrders(selectedStore.id);
      if (result.errors && result.errors > 0) {
        onMessage(`Sincronização finalizada com erros: ${result.synced} sincronizados, ${result.errors} erros. Veja logs do backend.`, 'error');
      } else {
        const byDesigner = result.syncedByDesigner && Object.keys(result.syncedByDesigner).length > 0
          ? ` (${Object.entries(result.syncedByDesigner).map(([name, n]) => `${name}: ${n}`).join(', ')})`
          : '';
        onMessage(`Sincronização concluída: ${result.synced} pedidos${byDesigner}. Se algum designer ficou com 0, confira em "Status de designer" se o status do Irroba está mapeado para ele.`, 'success');
      }
      loadData();
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      onMessage('Erro ao sincronizar pedidos', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncAllStores = async () => {
    setSyncing(true);
    try {
      const result = await apiService.syncAllDesignerOrders();
      if (result.errors && result.errors > 0) {
        onMessage(`Sincronização finalizada com erros: ${result.synced} sincronizados, ${result.errors} erros. Veja logs do backend.`, 'error');
      } else {
        const byStore = result.syncedByStore && Object.keys(result.syncedByStore).length > 0
          ? ` Lojas: ${Object.entries(result.syncedByStore).map(([name, n]) => `${name}: ${n}`).join(', ')}.`
          : '';
        const byDesigner = result.syncedByDesigner && Object.keys(result.syncedByDesigner).length > 0
          ? ` Designers: ${Object.entries(result.syncedByDesigner).map(([name, n]) => `${name}: ${n}`).join(', ')}.`
          : '';
        onMessage(`Sincronização de todas as lojas concluída: ${result.synced} pedidos.${byStore}${byDesigner}`, 'success');
      }
      loadData();
    } catch (error) {
      console.error('Erro ao sincronizar todas as lojas:', error);
      onMessage('Erro ao sincronizar pedidos', 'error');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={40} sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          🛒 Lojas IRROBA
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingStore(null);
            setStoreForm({ name: '', apiKey: '', apiSecret: '', apiUrl: 'https://api.irroba.com.br/v1', apiUsername: '' });
            setOpenStoreDialog(true);
          }}
          sx={{ background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)', color: '#1a1a1a', fontWeight: 600 }}
        >
          Nova Loja
        </Button>
      </Box>

      {stores.length === 0 ? (
        <Alert severity="info">
          Nenhuma loja IRROBA cadastrada. Clique em "Nova Loja" para adicionar.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Lista de Lojas */}
          <Grid item xs={12} md={4}>
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Lojas Cadastradas</Typography>
                {stores.map((store) => (
                  <Paper
                    key={store.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      border: selectedStore?.id === store.id ? '2px solid #D4AF37' : '1px solid #e5e7eb',
                      backgroundColor: selectedStore?.id === store.id ? 'rgba(212, 175, 55, 0.05)' : 'transparent',
                    }}
                    onClick={() => setSelectedStore(store)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{store.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {store._count?.designerStatus || 0} mapeamentos • {store._count?.orders || 0} pedidos
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingStore(store);
                            setStoreForm({ name: store.name, apiKey: '', apiSecret: '', apiUrl: store.apiUrl || 'https://api.irroba.com.br/v1', apiUsername: (store as any).apiUsername || '' });
                            setOpenStoreDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStore(store.id);
                          }}
                          sx={{ color: '#ef4444' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Mapeamentos da Loja Selecionada */}
          <Grid item xs={12} md={8}>
            {selectedStore ? (
              <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Mapeamentos: {selectedStore.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSyncAllStores}
                        disabled={syncing || stores.length === 0}
                        sx={{ background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)', color: '#1a1a1a' }}
                      >
                        {syncing ? 'Sincronizando...' : '🔄 Sincronizar TODAS as Lojas'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSyncOrders}
                        disabled={syncing || mappings.length === 0}
                      >
                        {syncing ? '...' : 'Sincronizar Esta Loja'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenMappingDialog(true)}
                        sx={{ background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)', color: '#1a1a1a' }}
                      >
                        Novo Mapeamento
                      </Button>
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Como funciona:</strong> Mapeie os status do IRROBA (ex: "Designer Responsável Paulo", ID: 15) 
                    para os designers do CRM. Quando um pedido tiver esse status, aparecerá na tela do designer.
                  </Alert>

                  {mappings.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                      Nenhum mapeamento configurado. Adicione mapeamentos para vincular status do IRROBA aos designers.
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                            <TableCell sx={{ fontWeight: 600 }}>ID Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Nome do Status (IRROBA)</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Designer (CRM)</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {mappings.map((mapping) => (
                            <TableRow key={mapping.id}>
                              <TableCell><Chip label={mapping.statusId} size="small" /></TableCell>
                              <TableCell>{mapping.statusName}</TableCell>
                              <TableCell>
                                <Chip label={mapping.designer.name} size="small" color="primary" />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteMapping(mapping.id)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">Selecione uma loja para ver os mapeamentos</Alert>
            )}
          </Grid>
        </Grid>
      )}

      {/* Dialog: Nova/Editar Loja */}
      <Dialog open={openStoreDialog} onClose={() => setOpenStoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingStore ? '✏️ Editar Loja' : '🛒 Nova Loja IRROBA'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Loja"
                value={storeForm.name}
                onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                placeholder="Ex: VettorPrint Principal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key (Usuário)"
                value={storeForm.apiKey}
                onChange={(e) => setStoreForm({ ...storeForm, apiKey: e.target.value })}
                placeholder="Ex: vettorpr_vettocrm"
                required={!editingStore}
                helperText={editingStore ? 'Deixe em branco para manter a atual' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Username (Webhook)"
                value={storeForm.apiUsername}
                onChange={(e) => setStoreForm({ ...storeForm, apiUsername: e.target.value })}
                placeholder='Ex: vettocrm (valor do api_username enviado no webhook IRROBA)'
                helperText="Opcional. Usado para identificar a loja quando o webhook envia api_username."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Secret (Senha)"
                type="password"
                value={storeForm.apiSecret}
                onChange={(e) => setStoreForm({ ...storeForm, apiSecret: e.target.value })}
                placeholder="Senha da API"
                required={!editingStore}
                helperText={editingStore ? 'Deixe em branco para manter a atual' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL da API"
                value={storeForm.apiUrl}
                onChange={(e) => setStoreForm({ ...storeForm, apiUrl: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStoreDialog(false)}>Cancelar</Button>
          <Button
            onClick={editingStore ? handleUpdateStore : handleCreateStore}
            variant="contained"
            disabled={!storeForm.name || (!editingStore && (!storeForm.apiKey || !storeForm.apiSecret))}
            sx={{ background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)', color: '#1a1a1a' }}
          >
            {editingStore ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Novo Mapeamento */}
      <Dialog open={openMappingDialog} onClose={() => setOpenMappingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>➕ Novo Mapeamento Status → Designer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Botão para buscar status */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleFetchStatuses}
                  disabled={loadingStatuses}
                  size="small"
                >
                  {loadingStatuses ? 'Buscando...' : '🔍 Buscar Status do IRROBA'}
                </Button>
                {availableStatuses.length > 0 && (
                  <Typography variant="caption" color="success.main">
                    {availableStatuses.length} status disponíveis
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Se tiver status disponíveis, mostrar como Select */}
            {availableStatuses.length > 0 ? (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Status do IRROBA</InputLabel>
                  <Select
                    value={mappingForm.statusId}
                    label="Status do IRROBA"
                    onChange={(e) => {
                      const status = availableStatuses.find(s => String(s.id) === e.target.value);
                      setMappingForm({
                        ...mappingForm,
                        statusId: e.target.value,
                        statusName: status?.name || '',
                      });
                    }}
                  >
                    {availableStatuses.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        <strong>ID {s.id}</strong>&nbsp;- {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="ID do Status"
                    type="number"
                    value={mappingForm.statusId}
                    onChange={(e) => setMappingForm({ ...mappingForm, statusId: e.target.value })}
                    placeholder="Ex: 15"
                    helperText="ID numérico do status no IRROBA"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Nome do Status"
                    value={mappingForm.statusName}
                    onChange={(e) => setMappingForm({ ...mappingForm, statusName: e.target.value })}
                    placeholder="Ex: Designer Responsável Paulo"
                    helperText="Nome exato como aparece no IRROBA"
                    required
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Designer no CRM</InputLabel>
                <Select
                  value={mappingForm.designerId}
                  label="Designer no CRM"
                  onChange={(e) => setMappingForm({ ...mappingForm, designerId: e.target.value })}
                >
                  {designers.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMappingDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateMapping}
            variant="contained"
            disabled={!mappingForm.statusId || !mappingForm.statusName || !mappingForm.designerId}
            sx={{ background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)', color: '#1a1a1a' }}
          >
            Criar Mapeamento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CHANGE_TYPE_CONFIG: Record<ChangeType, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  new:     { label: 'Novo',     color: '#065f46', bg: '#d1fae5', icon: <NewFeatureIcon     sx={{ fontSize: 14 }} /> },
  improve: { label: 'Melhoria', color: '#1e40af', bg: '#dbeafe', icon: <ImproveFeatureIcon sx={{ fontSize: 14 }} /> },
  fix:     { label: 'Correção', color: '#92400e', bg: '#fef3c7', icon: <FixFeatureIcon     sx={{ fontSize: 14 }} /> },
};

const Settings: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [changelogOpen, setChangelogOpen] = useState(false);
  
  // Estados de Usuários
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserForm, setEditUserForm] = useState<{ name: string; email: string; role: string; newPassword: string }>({
    name: '', email: '', role: 'SELLER', newPassword: '',
  });
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<NewUser>({
    name: '',
    email: '',
    password: '',
    role: 'SELLER',
  });

  // Estados de Sessão
  const [sessionHours, setSessionHours] = useState(8);
  const [sessionDays, setSessionDays] = useState(0);
  const [loadingSession, setLoadingSession] = useState(true);
  const [savingSession, setSavingSession] = useState(false);

  // Estados de Integrações
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [openIntegrationDialog, setOpenIntegrationDialog] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [testingIrroba, setTestingIrroba] = useState(false);
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    type: 'whatsapp',
    config: '',
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Admin e Gerência podem acessar as configurações
  const canAccessSettings = currentUser?.role === 'ADMIN' || currentUser?.role === 'MANAGER';

  // ==================== CARREGAR DADOS ====================

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await apiService.getUsers();
      setUsers(usersData);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao carregar usuários: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSessionSettings = async () => {
    try {
      setLoadingSession(true);
      const data = await apiService.getSessionDuration();
      setSessionHours(parseInt(data.hours) || 8);
      setSessionDays(parseInt(data.days) || 0);
    } catch (error: any) {
      console.error('Erro ao carregar configurações de sessão:', error);
    } finally {
      setLoadingSession(false);
    }
  };

  const loadIntegrations = async () => {
    try {
      setLoadingIntegrations(true);
      const data = await apiService.getAllIntegrations();
      setIntegrations(data);
    } catch (error: any) {
      console.error('Erro ao carregar integrações:', error);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  useEffect(() => {
    if (canAccessSettings) {
      loadUsers();
      loadSessionSettings();
      loadIntegrations();
    }
  }, [canAccessSettings]);

  // ==================== FUNÇÕES DE USUÁRIOS ====================

  const handleCreateUser = async () => {
    if (!canAccessSettings) return;

    try {
      await apiService.createUser({
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
      });

      await loadUsers();
      setNewUser({ name: '', email: '', password: '', role: 'SELLER' });
      setOpenDialog(false);

      setSnackbar({
        open: true,
        message: 'Usuário criado com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao criar usuário: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!canAccessSettings || !deleteUser) return;

    try {
      await apiService.deleteUser(deleteUser.id);
      await loadUsers();
      setOpenDeleteDialog(false);
      setDeleteUser(null);

      setSnackbar({
        open: true,
        message: 'Usuário excluído com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao excluir usuário: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    if (!canAccessSettings) return;

    try {
      const updatedUser = await apiService.toggleUserStatus(userId);
      await loadUsers();

      setSnackbar({
        open: true,
        message: `Usuário ${updatedUser.isActive ? 'ativado' : 'desativado'} com sucesso!`,
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao alterar status: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (user: User) => {
    setEditingUser(user);
    setEditUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      newPassword: '',
    });
    setOpenEditDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!canAccessSettings || !editingUser) return;

    try {
      const updateData: { name?: string; email?: string; role?: 'ADMIN' | 'SELLER' | 'DESIGNER' | 'SUPPORT' | 'MANAGER' | 'PRODUCAO' } = {};
      if (editUserForm.name !== editingUser.name) updateData.name = editUserForm.name;
      if (editUserForm.email !== editingUser.email) updateData.email = editUserForm.email;
      if (editUserForm.role !== editingUser.role) updateData.role = editUserForm.role as 'ADMIN' | 'SELLER' | 'DESIGNER' | 'SUPPORT' | 'MANAGER' | 'PRODUCAO';

      if (Object.keys(updateData).length > 0) {
        await apiService.updateUser(editingUser.id, updateData);
      }
      if (editUserForm.newPassword && editUserForm.newPassword.length >= 6) {
        await apiService.changeUserPassword(editingUser.id, editUserForm.newPassword);
      }

      await loadUsers();
      setOpenEditDialog(false);
      setEditingUser(null);

      setSnackbar({
        open: true,
        message: 'Usuário atualizado com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar usuário: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  // ==================== FUNÇÕES DE SESSÃO ====================

  const handleSaveSession = async () => {
    try {
      setSavingSession(true);
      await apiService.updateSessionDuration(sessionHours, sessionDays);

      setSnackbar({
        open: true,
        message: 'Configuração de sessão salva! As novas configurações serão aplicadas nos próximos logins.',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao salvar configuração: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    } finally {
      setSavingSession(false);
    }
  };

  // ==================== FUNÇÕES DE INTEGRAÇÕES ====================

  const handleCreateIntegration = async () => {
    try {
      const integrationType = newIntegration.name.toLowerCase().includes('irroba')
        ? 'irroba'
        : newIntegration.type;
      if (!validateIntegrationConfig(integrationType)) {
        return;
      }
      await apiService.createIntegration({
        name: newIntegration.name,
        type: integrationType,
        config: newIntegration.config || undefined,
      });

      await loadIntegrations();
      setNewIntegration({ name: '', type: 'whatsapp', config: '' });
      setOpenIntegrationDialog(false);

      setSnackbar({
        open: true,
        message: 'Integração criada com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao criar integração: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleUpdateIntegration = async () => {
    if (!editingIntegration) return;

    try {
      const integrationType = newIntegration.name.toLowerCase().includes('irroba')
        ? 'irroba'
        : newIntegration.type;
      if (!validateIntegrationConfig(integrationType)) {
        return;
      }
      await apiService.updateIntegration(editingIntegration.id, {
        name: newIntegration.name,
        type: integrationType,
        config: newIntegration.config || undefined,
      });

      await loadIntegrations();
      setNewIntegration({ name: '', type: 'whatsapp', config: '' });
      setEditingIntegration(null);
      setOpenIntegrationDialog(false);

      setSnackbar({
        open: true,
        message: 'Integração atualizada com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar integração: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleTestIrroba = async () => {
    if (!editingIntegration) {
      setSnackbar({
        open: true,
        message: 'Salve a integração antes de testar.',
        severity: 'error',
      });
      return;
    }

    try {
      setTestingIrroba(true);
      const result = await apiService.testIrrobaConnection();
      setSnackbar({
        open: true,
        message: result.message || 'Teste concluído.',
        severity: result.success ? 'success' : 'error',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao testar integração: ' + (error.response?.data?.message || error.response?.data?.error || error.message),
        severity: 'error',
      });
    } finally {
      setTestingIrroba(false);
    }
  };

  const validateIntegrationConfig = (integrationType: string) => {
    const rawConfig = newIntegration.config?.trim();
    if (!rawConfig) {
      if (integrationType === 'irroba') {
        setSnackbar({
          open: true,
          message: 'Informe o JSON da integração do IRROBA.',
          severity: 'error',
        });
        return false;
      }
      return true;
    }

    const normalized = rawConfig.startsWith('{') ? rawConfig : `{${rawConfig}}`;
    try {
      const parsed = JSON.parse(normalized);
      if (integrationType === 'irroba') {
        const hasRequired = parsed.username && parsed.password && parsed.baseUrl;
        if (!hasRequired) {
          setSnackbar({
            open: true,
            message: 'JSON do IRROBA inválido. Informe username, password e baseUrl.',
            severity: 'error',
          });
          return false;
        }
      }
      return true;
    } catch {
      setSnackbar({
        open: true,
        message: 'Configuração JSON inválida. Verifique as chaves e as aspas.',
        severity: 'error',
      });
      return false;
    }
  };

  const handleToggleIntegration = async (id: string) => {
    try {
      await apiService.toggleIntegration(id);
      await loadIntegrations();

      setSnackbar({
        open: true,
        message: 'Status da integração alterado!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao alterar status: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const handleDeleteIntegration = async (id: string) => {
    try {
      await apiService.deleteIntegration(id);
      await loadIntegrations();

      setSnackbar({
        open: true,
        message: 'Integração removida com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: 'Erro ao remover integração: ' + (error.response?.data?.error || error.message),
        severity: 'error',
      });
    }
  };

  const openEditIntegration = (integration: Integration) => {
    setEditingIntegration(integration);
    setNewIntegration({
      name: integration.name,
      type: integration.type,
      config: integration.config || '',
    });
    setOpenIntegrationDialog(true);
  };

  // ==================== HELPERS ====================

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return '#D4AF37';
      case 'SELLER': return '#10b981';
      case 'DESIGNER': return '#ec4899';
      case 'SUPPORT': return '#3b82f6';
      case 'MANAGER': return '#0d9488';
      case 'PRODUCAO': return '#7c3aed';
      default: return '#6b7280';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'Administrador';
      case 'SELLER': return 'Vendedor';
      case 'DESIGNER': return 'Designer';
      case 'SUPPORT': return 'Suporte';
      case 'MANAGER': return 'Gerência';
      case 'PRODUCAO': return 'Produção';
      default: return role;
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getIntegrationTypeLabel = (type: string) => {
    switch (type) {
      case 'whatsapp': return 'WhatsApp';
      case 'email': return 'E-mail';
      case 'sms': return 'SMS';
      case 'webhook': return 'Webhook';
      case 'api': return 'API Externa';
      default: return type;
    }
  };

  const getIntegrationIcon = (type: string) => {
    switch (type) {
      case 'whatsapp': return '📱';
      case 'email': return '📧';
      case 'sms': return '💬';
      case 'webhook': return '🔗';
      case 'api': return '🌐';
      default: return '⚙️';
    }
  };

  const formatSessionDuration = () => {
    const totalHours = sessionHours + (sessionDays * 24);
    if (sessionDays > 0) {
      return `${sessionDays} dia(s) e ${sessionHours} hora(s) (${totalHours}h total)`;
    }
    return `${sessionHours} hora(s)`;
  };

  if (!canAccessSettings) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 120px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          py: 6,
          background: 'linear-gradient(180deg, rgba(212, 175, 55, 0.04) 0%, transparent 50%)',
        }}
      >
        <Box
          sx={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            bgcolor: 'rgba(158, 158, 158, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 100, color: 'rgba(158, 158, 158, 0.9)' }} />
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: '#1a1a1a',
            mb: 1.5,
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
          }}
        >
          Sem permissão de acesso
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 360 }}>
          Apenas administradores e gerência podem acessar as configurações do sistema. Entre em contato com o administrador se precisar de acesso.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
          ⚙️ Configurações do Sistema
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Gerencie usuários, sessões e integrações do CRM
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
            },
            '& .Mui-selected': {
              color: '#D4AF37',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#D4AF37',
            },
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Usuários" />
          <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Sessão" />
          <Tab icon={<IntegrationIcon />} iconPosition="start" label="Integrações" />
          <Tab icon={<LinkIcon />} iconPosition="start" label="Lojas IRROBA" />
          <Tab icon={<InfoIcon />} iconPosition="start" label="Sobre" />
        </Tabs>
      </Box>

      {/* Tab Usuários */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filtrar por função</InputLabel>
              <Select
                value={roleFilter}
                label="Filtrar por função"
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{ backgroundColor: '#fff' }}
              >
                <MenuItem value="">Todas as funções</MenuItem>
                <MenuItem value="ADMIN">Administrador</MenuItem>
                <MenuItem value="MANAGER">Gerência</MenuItem>
                <MenuItem value="SELLER">Vendedor</MenuItem>
                <MenuItem value="DESIGNER">Designer</MenuItem>
                <MenuItem value="SUPPORT">Suporte</MenuItem>
                <MenuItem value="PRODUCAO">Produção</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              color: '#1a1a1a',
              fontWeight: 600,
            }}
          >
            Novo Usuário
          </Button>
        </Box>

        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              👥 Gerenciamento de Usuários
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Função</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Criado em</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={40} sx={{ color: '#D4AF37' }} />
                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                          Carregando usuários...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (() => {
                    const filtered = roleFilter ? users.filter(u => u.role === roleFilter) : users;
                    const roleOrder = ['ADMIN', 'MANAGER', 'SELLER', 'DESIGNER', 'SUPPORT', 'PRODUCAO'];
                    const grouped = roleFilter
                      ? filtered
                      : roleOrder.flatMap(role => users.filter(u => u.role === role)).filter(Boolean);
                    if (grouped.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {roleFilter ? 'Nenhum usuário encontrado para esta função' : 'Nenhum usuário encontrado'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return grouped.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: getRoleColor(user.role),
                                width: 32,
                                height: 32,
                                fontSize: '14px',
                                fontWeight: 600,
                              }}
                            >
                              {getInitials(user.name)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {user.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(user.role)}
                            sx={{
                              backgroundColor: `${getRoleColor(user.role)}20`,
                              color: getRoleColor(user.role),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? 'Ativo' : 'Inativo'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{new Date(user.createdAt).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>
                          <Tooltip title="Editar usuário">
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenEditDialog(user)}
                              sx={{
                                borderColor: '#D4AF37',
                                color: '#D4AF37',
                                '&:hover': {
                                  borderColor: '#c49927',
                                  backgroundColor: 'rgba(212, 175, 55, 0.08)',
                                },
                              }}
                            >
                              Editar
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab Sessão */}
      <TabPanel value={activeTab} index={1}>
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              ⏱️ Tempo de Sessão
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              Configure por quanto tempo os usuários permanecerão logados antes de precisar fazer login novamente.
            </Typography>

            {loadingSession ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} sx={{ color: '#D4AF37' }} />
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Dias: {sessionDays}
                  </Typography>
                  <Slider
                    value={sessionDays}
                    onChange={(_, value) => setSessionDays(value as number)}
                    min={0}
                    max={30}
                    step={1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 7, label: '7' },
                      { value: 14, label: '14' },
                      { value: 30, label: '30' },
                    ]}
                    sx={{
                      color: '#D4AF37',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#D4AF37',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Horas: {sessionHours}
                  </Typography>
                  <Slider
                    value={sessionHours}
                    onChange={(_, value) => setSessionHours(value as number)}
                    min={1}
                    max={24}
                    step={1}
                    marks={[
                      { value: 1, label: '1h' },
                      { value: 8, label: '8h' },
                      { value: 12, label: '12h' },
                      { value: 24, label: '24h' },
                    ]}
                    sx={{
                      color: '#D4AF37',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#D4AF37',
                      },
                    }}
                  />
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Duração atual:</strong> {formatSessionDuration()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Após este período, os usuários serão deslogados automaticamente.
                  </Typography>
                </Alert>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSession}
                  disabled={savingSession}
                  sx={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                    color: '#1a1a1a',
                    fontWeight: 600,
                  }}
                >
                  {savingSession ? 'Salvando...' : 'Salvar Configuração'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab Integrações */}
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingIntegration(null);
              setNewIntegration({ name: '', type: 'whatsapp', config: '' });
              setOpenIntegrationDialog(true);
            }}
            sx={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              color: '#1a1a1a',
              fontWeight: 600,
            }}
          >
            Nova Integração
          </Button>
        </Box>

        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              🔗 Integrações
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Configure integrações com serviços externos como WhatsApp, e-mail, webhooks e APIs.
            </Typography>

            {loadingIntegrations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} sx={{ color: '#D4AF37' }} />
              </Box>
            ) : integrations.length === 0 ? (
              <Alert severity="info">
                Nenhuma integração configurada. Clique em "Nova Integração" para adicionar.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {integrations.map((integration) => (
                  <Grid item xs={12} md={6} key={integration.id}>
                    <Paper
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: integration.isEnabled ? '#10b981' : '#e5e7eb',
                        borderRadius: 2,
                        backgroundColor: integration.isEnabled ? 'rgba(16, 185, 129, 0.05)' : 'transparent',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Typography variant="h4">
                            {getIntegrationIcon(integration.type)}
                          </Typography>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {integration.name}
                            </Typography>
                            <Chip
                              label={getIntegrationTypeLabel(integration.type)}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <Switch
                            checked={integration.isEnabled}
                            onChange={() => handleToggleIntegration(integration.id)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#10b981',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#10b981',
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => openEditIntegration(integration)}
                            sx={{ color: '#D4AF37' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteIntegration(integration.id)}
                            sx={{ color: '#ef4444' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          icon={integration.isEnabled ? <LinkIcon /> : <BlockIcon />}
                          label={integration.isEnabled ? 'Conectado' : 'Desconectado'}
                          size="small"
                          color={integration.isEnabled ? 'success' : 'default'}
                        />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Criado em {new Date(integration.createdAt).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab Lojas IRROBA */}
      <TabPanel value={activeTab} index={3}>
        <IrrobaStoresTab 
          onMessage={(message, severity) => setSnackbar({ open: true, message, severity })} 
        />
      </TabPanel>

      {/* Create User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          👤 Criar Novo Usuário
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome Completo"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Senha"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Função</InputLabel>
                <Select
                  value={newUser.role}
                  label="Função"
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as any })}
                >
                  <MenuItem value="SELLER">Vendedor</MenuItem>
                  <MenuItem value="DESIGNER">Designer</MenuItem>
                  <MenuItem value="SUPPORT">Suporte</MenuItem>
                  <MenuItem value="MANAGER">Gerência</MenuItem>
                  <MenuItem value="PRODUCAO">Produção</MenuItem>
                  <MenuItem value="ADMIN">Administrador</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!newUser.name || !newUser.email || !newUser.password}
            sx={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              color: '#1a1a1a',
            }}
          >
            Criar Usuário
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setEditingUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          ✏️ Editar Usuário — {editingUser?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                value={editUserForm.name}
                onChange={(e) => setEditUserForm(f => ({ ...f, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Função</InputLabel>
                <Select
                  value={editUserForm.role}
                  label="Função"
                  onChange={(e) => setEditUserForm(f => ({ ...f, role: e.target.value }))}
                  disabled={editingUser?.id === currentUser?.id}
                >
                  <MenuItem value="SELLER">Vendedor</MenuItem>
                  <MenuItem value="DESIGNER">Designer</MenuItem>
                  <MenuItem value="SUPPORT">Suporte</MenuItem>
                  <MenuItem value="MANAGER">Gerência</MenuItem>
                  <MenuItem value="PRODUCAO">Produção</MenuItem>
                  <MenuItem value="ADMIN">Administrador</MenuItem>
                </Select>
                {editingUser?.id === currentUser?.id && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    Não é possível alterar sua própria função
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Status</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingUser?.isActive ?? true}
                      onChange={async () => {
                        if (!editingUser || editingUser.id === currentUser?.id) return;
                        try {
                          await apiService.toggleUserStatus(editingUser.id);
                          await loadUsers();
                          setEditingUser(prev => prev ? { ...prev, isActive: !prev.isActive } : null);
                          setSnackbar({ open: true, message: 'Status alterado com sucesso!', severity: 'success' });
                        } catch (err: any) {
                          setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao alterar status', severity: 'error' });
                        }
                      }}
                      disabled={editingUser?.id === currentUser?.id}
                      color="primary"
                    />
                  }
                  label={editingUser?.isActive ? 'Ativo' : 'Inativo'}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Alterar senha (opcional)</Typography>
              <TextField
                fullWidth
                label="Nova senha"
                type="password"
                value={editUserForm.newPassword}
                onChange={(e) => setEditUserForm(f => ({ ...f, newPassword: e.target.value }))}
                placeholder="Deixe em branco para manter a atual"
                helperText="Mínimo 6 caracteres"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Zona de perigo</Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setOpenEditDialog(false);
                  setDeleteUser(editingUser);
                  setEditingUser(null);
                  setOpenDeleteDialog(true);
                }}
                disabled={editingUser?.id === currentUser?.id}
                fullWidth
              >
                Excluir usuário
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenEditDialog(false); setEditingUser(null); }} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpdateUser}
            sx={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              color: '#1a1a1a',
            }}
          >
            Salvar alterações
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#ef4444' }}>
          ⚠️ Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Você está prestes a excluir o usuário <strong>{deleteUser?.name}</strong>.
            Esta ação não pode ser desfeita.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Excluir Usuário
          </Button>
        </DialogActions>
      </Dialog>

      {/* Integration Dialog */}
      <Dialog
        open={openIntegrationDialog}
        onClose={() => {
          setOpenIntegrationDialog(false);
          setEditingIntegration(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          {editingIntegration ? '✏️ Editar Integração' : '🔗 Nova Integração'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Integração"
                value={newIntegration.name}
                onChange={(e) => setNewIntegration({ ...newIntegration, name: e.target.value })}
                required
                placeholder="Ex: WhatsApp Business"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={newIntegration.type}
                  label="Tipo"
                  onChange={(e) => setNewIntegration({ ...newIntegration, type: e.target.value })}
                >
                  <MenuItem value="whatsapp">📱 WhatsApp</MenuItem>
                  <MenuItem value="email">📧 E-mail</MenuItem>
                  <MenuItem value="sms">💬 SMS</MenuItem>
                  <MenuItem value="webhook">🔗 Webhook</MenuItem>
                  <MenuItem value="api">🌐 API Externa</MenuItem>
                  <MenuItem value="irroba">🛒 IRROBA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {(newIntegration.type === 'irroba' || newIntegration.name.toLowerCase().includes('irroba')) && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Use os dados da tela <strong>Usuários API</strong> do Irroba: <strong>Usuário</strong> → <code>username</code>, <strong>Senha</strong> → <code>password</code>. Webhooks no Irroba: Pedidos e Produtos apontando para sua API (ex.: https://api.vettorchat.com.br/api/integrations/irroba/webhook/orders e /products).
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Configuração (JSON)"
                value={newIntegration.config}
                onChange={(e) => setNewIntegration({ ...newIntegration, config: e.target.value })}
                multiline
                rows={4}
                placeholder={
                  newIntegration.type === 'irroba'
                    ? '{"username": "vettorpr_vettocrm", "password": "sua_senha_api", "baseUrl": "https://api.irroba.com.br/v1"}'
                    : '{"api_key": "...", "endpoint": "..."}'
                }
                helperText={
                  newIntegration.type === 'irroba'
                    ? 'Obrigatório: username (Usuário API do Irroba), password (Senha API), baseUrl (geralmente https://api.irroba.com.br/v1)'
                    : 'Configure as credenciais e parâmetros da integração em formato JSON'
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenIntegrationDialog(false);
              setEditingIntegration(null);
            }}
            startIcon={<CancelIcon />}
          >
            Cancelar
          </Button>
          {(newIntegration.type === 'irroba' || newIntegration.name.toLowerCase().includes('irroba')) && (
            <Button
              onClick={handleTestIrroba}
              startIcon={<LinkIcon />}
              disabled={testingIrroba}
            >
              {testingIrroba ? 'Testando...' : 'Testar IRROBA'}
            </Button>
          )}
          <Button
            onClick={editingIntegration ? handleUpdateIntegration : handleCreateIntegration}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!newIntegration.name}
            sx={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              color: '#1a1a1a',
            }}
          >
            {editingIntegration ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tab Sobre */}
      <TabPanel value={activeTab} index={4}>
        <Box sx={{ maxWidth: 760 }}>

          {/* Cabeçalho da versão */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <RocketIcon sx={{ color: 'white', fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', lineHeight: 1 }}>
                  VettorPrint CRM
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  Histórico de Atualizações
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip
                icon={<NewReleasesIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />}
                label={`Versão atual: v${APP_VERSION}`}
                sx={{
                  backgroundColor: 'rgba(245, 158, 11, 0.2)',
                  color: '#fbbf24',
                  fontWeight: 700,
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  fontSize: '12px',
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setChangelogOpen(true)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  textTransform: 'none',
                  '&:hover': { borderColor: 'white', color: 'white', backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
              >
                Ver popup de novidades
              </Button>
            </Box>
          </Paper>

          {/* Lista de versões */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {CHANGELOG.map((entry, entryIdx) => (
              <Card
                key={entry.version}
                elevation={0}
                sx={{
                  border: entryIdx === 0 ? '1.5px solid #D4AF37' : '1px solid #e5e7eb',
                  borderRadius: 3,
                  overflow: 'hidden',
                }}
              >
                {/* Header da versão */}
                <Box
                  sx={{
                    px: 3,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: entryIdx === 0 ? 'rgba(212, 175, 55, 0.06)' : '#f9fafb',
                    borderBottom: '1px solid',
                    borderColor: entryIdx === 0 ? 'rgba(212, 175, 55, 0.2)' : '#f1f5f9',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      label={`v${entry.version}`}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        backgroundColor: entryIdx === 0 ? 'rgba(212, 175, 55, 0.15)' : '#e2e8f0',
                        color: entryIdx === 0 ? '#92400e' : '#475569',
                      }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {entry.title}
                    </Typography>
                    {entryIdx === 0 && (
                      <Chip label="Atual" size="small" sx={{ backgroundColor: '#d1fae5', color: '#065f46', fontSize: '10px', fontWeight: 700 }} />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    {entry.date}
                  </Typography>
                </Box>

                {/* Itens da versão */}
                <CardContent sx={{ px: 3, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {entry.items.map((item, idx) => {
                    const cfg = CHANGE_TYPE_CONFIG[item.type];
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          py: 0.9,
                          borderBottom: idx < entry.items.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            px: 0.8,
                            py: 0.3,
                            borderRadius: 1,
                            backgroundColor: cfg.bg,
                            color: cfg.color,
                            flexShrink: 0,
                            minWidth: 72,
                            justifyContent: 'center',
                            mt: 0.1,
                          }}
                        >
                          {cfg.icon}
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: cfg.color }}>
                            {cfg.label}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.5 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </TabPanel>

      <ChangelogModal open={changelogOpen} onClose={() => setChangelogOpen(false)} />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
